import React, { memo, useEffect, useMemo, useRef, useState } from 'react';
import { DesktopOutlined, MobileOutlined } from '@ant-design/icons';
import { Segmented, Spin, message } from 'antd';
import { useSelector } from 'react-redux';
import {
  ONSITE_PREVIEW_DEFAULT_BG,
  ONSITE_PREVIEW_MOBILE_WIDTH_PX,
  openOnSiteLivePreviewTab,
  prepareOnSitePreviewHtml,
} from '../onsiteLivePreview';

/**
 * On-Site preview.
 * - openInNewTab: PREVIEW button — full browser tab (accurate live placement)
 * - otherwise: Desktop/Mobile viewport without fake device chrome (launch page)
 */
function OnSitePreview({
  variation,
  openInNewTab = false,
  onPreviewOpened,
  defaultMode = 'desktop',
  backgroundColor = ONSITE_PREVIEW_DEFAULT_BG,
}) {
  const { html = '', configuration: previewConfiguration } = useSelector(
    (state) => state.engage.communication.communicationPreview
  );
  const sizeConfig = variation?.configuration || previewConfiguration || {};

  const [previewType, setPreviewType] = useState(defaultMode);
  const [bgColor, setBgColor] = useState(backgroundColor);
  const [popupBlocked, setPopupBlocked] = useState(false);
  const openedRef = useRef(false);

  const previewHtml = useMemo(
    () =>
      prepareOnSitePreviewHtml(html, {
        configuration: sizeConfig,
      }),
    [html, sizeConfig]
  );

  useEffect(() => {
    if (!openInNewTab || !html || openedRef.current) return;
    openedRef.current = true;

    const win = openOnSiteLivePreviewTab(html, {
      defaultMode,
      backgroundColor: bgColor,
      configuration: sizeConfig,
    });
    if (win) {
      onPreviewOpened?.();
      return;
    }

    setPopupBlocked(true);
    message.warning(
      'Popup blocked — showing inline preview. Allow popups for a full-page preview.'
    );
  }, [openInNewTab, html, defaultMode, bgColor, sizeConfig, onPreviewOpened]);

  if (openInNewTab && !html) {
    return (
      <div
        style={{
          minWidth: 320,
          minHeight: 200,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: '#fff',
          borderRadius: 8,
          padding: 24,
        }}
      >
        <Spin tip="Opening live preview…" />
      </div>
    );
  }

  if (openInNewTab && html && !popupBlocked) {
    return (
      <div
        style={{
          minWidth: 280,
          padding: 24,
          background: '#fff',
          borderRadius: 8,
          textAlign: 'center',
          color: '#555',
          fontSize: 13,
        }}
      >
        Live preview opened in a new tab.
      </div>
    );
  }

  const isMobile = previewType === 'mobile';

  return (
    <section
      style={{
        background: bgColor,
        borderRadius: 8,
        padding: 12,
        minWidth: isMobile ? ONSITE_PREVIEW_MOBILE_WIDTH_PX + 48 : 640,
        maxWidth: '90vw',
      }}
    >
      <div
        style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          paddingBottom: 12,
          gap: 12,
          flexWrap: 'wrap',
        }}
      >
        <Segmented
          style={{ borderRadius: 8 }}
          value={previewType}
          onChange={setPreviewType}
          options={[
            { value: 'mobile', icon: <MobileOutlined /> },
            { value: 'desktop', icon: <DesktopOutlined /> },
          ]}
          className="preview-switcher"
        />
        <label
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 6,
            fontSize: 12,
            color: '#444',
          }}
          title="Page background behind the onsite message"
        >
          BG
          <input
            type="color"
            value={
              /^#([0-9a-fA-F]{6})$/.test(bgColor)
                ? bgColor
                : ONSITE_PREVIEW_DEFAULT_BG
            }
            onChange={(e) => setBgColor(e.target.value)}
            style={{
              width: 28,
              height: 28,
              padding: 0,
              border: '1px solid #ccc',
              borderRadius: 4,
              cursor: 'pointer',
            }}
          />
        </label>
        <span style={{ fontSize: 12, color: '#666' }}>
          {isMobile
            ? `Mobile (${ONSITE_PREVIEW_MOBILE_WIDTH_PX}px)`
            : 'Desktop (full width)'}
        </span>
      </div>
      <div
        style={{
          display: 'flex',
          justifyContent: 'center',
          backgroundColor: bgColor,
          borderRadius: 8,
          overflow: 'hidden',
          height: 'min(72vh, 720px)',
        }}
      >
        {previewHtml ? (
          <iframe
            key={JSON.stringify(sizeConfig?.fallbackMetric || {})}
            title="On-Site live preview"
            srcDoc={previewHtml}
            sandbox="allow-same-origin allow-scripts allow-popups"
            style={{
              width: isMobile ? ONSITE_PREVIEW_MOBILE_WIDTH_PX : '100%',
              maxWidth: '100%',
              height: '100%',
              border: 0,
              background: 'transparent',
              borderRadius: isMobile ? 12 : 0,
              boxShadow: isMobile ? '0 8px 32px rgba(0,0,0,0.18)' : 'none',
            }}
          />
        ) : (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: '100%',
              color: '#888',
            }}
          >
            <Spin />
          </div>
        )}
      </div>
    </section>
  );
}

const Memoized = memo(OnSitePreview);

export { Memoized as OnSitePreview };
